import { isEditableVideoTemplate } from './Template.model';

export const TEMPLATES_LIST_SEARCH_STORAGE_KEY = 'templates-list-search';
export const DEFAULT_STATUS_FILTER = 'live';

export function emptyTemplateFilters() {
  return {
    postType: '',
    topic: '',
    subtopic: '',
    availability: '',
    type: '',
    dateFrom: '',
    dateTo: '',
    statuses: DEFAULT_STATUS_FILTER,
    sortBy: '',
    types: '',
  };
}

const FILTER_FIELDS = Object.keys(emptyTemplateFilters());

export function templateFiltersEqual(a, b) {
  return FILTER_FIELDS.every((field) => (a[field] || '') === (b[field] || ''));
}

export function normalizeFilterId(value) {
  if (value == null || value === '') {
    return '';
  }

  if (typeof value === 'object' && value.id != null) {
    return String(value.id);
  }

  return String(value);
}

export function parseTemplateFiltersFromSearchParams(searchParams) {
  const postType = searchParams.get('postType') || '';
  const types =
    searchParams.get('types') ||
    (postType && postType !== 'all' ? postType : '');

  return {
    ...emptyTemplateFilters(),
    postType,
    topic:
      postType === 'pl' ? '' : normalizeFilterId(searchParams.get('topic')),
    subtopic:
      postType === 'pl' ? '' : normalizeFilterId(searchParams.get('subtopic')),
    availability: searchParams.get('availability') || '',
    type: searchParams.get('type') || '',
    dateFrom: searchParams.get('dateFrom') || '',
    dateTo: searchParams.get('dateTo') || '',
    statuses: searchParams.get('statuses') || DEFAULT_STATUS_FILTER,
    sortBy: searchParams.get('sortBy') || '',
    types,
  };
}

export function buildTemplateFiltersSearchParams(filters, searchQuery = '') {
  const params = new URLSearchParams();

  if (filters.postType) {
    params.set('postType', filters.postType);
  }

  if (filters.postType !== 'pl') {
    if (filters.topic) {
      params.set('topic', normalizeFilterId(filters.topic));
    }
    if (filters.subtopic) {
      params.set('subtopic', normalizeFilterId(filters.subtopic));
    }
  }

  if (filters.availability) {
    params.set('availability', filters.availability);
  }

  if (filters.type) {
    params.set('type', filters.type);
  }

  if (filters.dateFrom) {
    params.set('dateFrom', filters.dateFrom);
  }

  if (filters.dateTo) {
    params.set('dateTo', filters.dateTo);
  }

  if (filters.statuses) {
    params.set('statuses', filters.statuses);
  }

  if (filters.sortBy) {
    params.set('sortBy', filters.sortBy);
  }

  if (filters.types) {
    params.set('types', filters.types);
  }

  if (searchQuery) {
    params.set('search', searchQuery);
  }

  return params;
}

export function buildTemplatesListSearch(
  filters,
  searchQuery = '',
  showInactive = false
) {
  const params = buildTemplateFiltersSearchParams(filters, searchQuery);

  if (showInactive) {
    params.set('showInactive', 'true');
  }

  const search = params.toString();

  return search ? `?${search}` : '';
}

export function persistTemplatesListSearch(search) {
  if (search) {
    sessionStorage.setItem(TEMPLATES_LIST_SEARCH_STORAGE_KEY, search);
    return;
  }

  sessionStorage.removeItem(TEMPLATES_LIST_SEARCH_STORAGE_KEY);
}

export function readTemplatesListSearch(location) {
  if (location.state?.templatesListSearch !== undefined) {
    return location.state.templatesListSearch;
  }

  if (location.search) {
    return location.search;
  }

  return sessionStorage.getItem(TEMPLATES_LIST_SEARCH_STORAGE_KEY) || '';
}

export function templatesListSearchToURLSearchParams(search = '') {
  const normalized = search.startsWith('?') ? search.slice(1) : search;

  return new URLSearchParams(normalized);
}

export function hasAppliedListFilters(filters, searchQuery = '') {
  const hasNonDefaultStatus =
    Boolean(filters.statuses) && filters.statuses !== DEFAULT_STATUS_FILTER;

  return Boolean(
    filters.postType ||
      filters.topic ||
      filters.subtopic ||
      filters.availability ||
      filters.type ||
      filters.dateFrom ||
      filters.dateTo ||
      hasNonDefaultStatus ||
      filters.sortBy ||
      filters.types ||
      searchQuery
  );
}

export function filtersForTypes(filters, types) {
  const selected = String(types || '')
    .split(',')
    .filter(Boolean);
  const categories = selected.filter((value) => value !== 'video');
  const postType =
    selected.length === 1 && categories.length === 1 ? categories[0] : '';

  return {
    ...filters,
    types,
    postType,
    topic: postType === 'pl' ? '' : filters.topic,
    subtopic: postType === 'pl' ? '' : filters.subtopic,
  };
}

export function buildTemplateFiltersFromTemplate(template = {}) {
  const types = isEditableVideoTemplate(template)
    ? 'video'
    : template.category || '';

  return filtersForTypes(
    {
      ...emptyTemplateFilters(),
      topic: normalizeFilterId(template.topic),
      subtopic: normalizeFilterId(template.subtopic),
    },
    types
  );
}

export function resolveTemplatesListSearchOnCancel(existingSearch, template) {
  const params = templatesListSearchToURLSearchParams(existingSearch);
  const existingFilters = parseTemplateFiltersFromSearchParams(params);
  const existingSearchQuery = params.get('search') || '';

  if (
    hasAppliedListFilters(existingFilters, existingSearchQuery) ||
    params.get('showInactive') === 'true'
  ) {
    return (
      existingSearch ||
      buildTemplatesListSearch(
        existingFilters,
        existingSearchQuery,
        params.get('showInactive') === 'true'
      )
    );
  }

  if (template) {
    return buildTemplatesListSearch(buildTemplateFiltersFromTemplate(template));
  }

  return '';
}

export function resolveTemplatesListSearchAfterSave(savedTemplate) {
  if (!savedTemplate) {
    return '';
  }

  return buildTemplatesListSearch(
    buildTemplateFiltersFromTemplate(savedTemplate)
  );
}
